import {
  GetViewerDocument,
  useCheckValidationCodeMutation,
  useRequestValidationCodeMutation,
  useUpdateEmailMutation,
  ValidationCodeResponseStatus,
} from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/base/dialog';
import { FormField } from '@/components/base/form/form-field';
import { Input } from '@/components/base/form/input';
import { Otp } from '@/components/base/otp';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import { CredentialsContext } from '@/context/credentials-context';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Transition } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearch } from '@tanstack/react-router';
import { useSnackbar } from 'notistack';
import {
  type FormEvent,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

interface EmailCodeComponentProps {
  email: string;
}

export function EmailVerification({
  email,
}: PropsWithChildren<EmailCodeComponentProps>): JSX.Element {
  const search: { verificationCode: string | number } = useSearch({
    strict: false,
  });
  const { verificationCode } = search;
  const isVerificationCode = Boolean(verificationCode);
  const [isOpen, setIsOpen] = useState(isVerificationCode);
  const [otp, setOtp] = useState(
    isVerificationCode ? verificationCode.toString() : '',
  );
  const { t } = useTranslation();
  const [requestValidationCode] = useRequestValidationCodeMutation();
  const [checkValidationCode] = useCheckValidationCodeMutation();
  const { enqueueSnackbar } = useSnackbar();
  const [codeWasSent, setCodeWasSent] = useState(false);
  const [otpError, setOtpError] = useState<null | string>(null);
  const [changeEmail, setChangeEmail] = useState(false);

  async function resendValidationCode(): Promise<void> {
    try {
      const { data } = await requestValidationCode();
      if (
        data?.requestValidationCode.status === ValidationCodeResponseStatus.Sent
      ) {
        setCodeWasSent(true);
      }
      setCodeWasSent(true);
    } catch (e) {
      enqueueSnackbar({
        message: 'Invalid Credentials',
        variant: 'error',
      });
    }
  }

  async function checkCode(): Promise<void> {
    try {
      const { data } = await checkValidationCode({
        variables: { code: otp },
        refetchQueries: [{ query: GetViewerDocument }],
      });
      if (data?.checkValidationCode.emailVerifiedAt) {
        setIsOpen(false);
        setOtpError(null);
      } else {
        setOtpError('Invalid code');
      }
    } catch (e) {
      enqueueSnackbar({
        message: 'Invalid Credentials',
        variant: 'error',
      });
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLSpanElement>): void {
    return void (async () => {
      if (event.key === 'Enter') {
        await resendValidationCode();
      }
    })();
  }

  function handleClickResend(): void {
    return void (async () => {
      await resendValidationCode();
    })();
  }

  function handleSubmitOtp(): void {
    return void (async () => {
      await checkCode();
    })();
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value) => {
        setIsOpen(value);
      }}
    >
      <DialogTrigger
        asChild
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <Section className="relative">
          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-5">
              <span className="fa-layers fa-fw mt-1.5">
                <FontAwesomeIcon icon={faEnvelope} className="h-7 w-7" />
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="h-4 w-4 translate-x-full translate-y-1/2 rounded-full bg-primary"
                />
              </span>
              <div className="flex flex-col gap-3">
                <PageHeading
                  subheadingClassName="text-default-gray-200"
                  size="small"
                  subheading={t('verifyEmail.verifyEmail')}
                >
                  {t('verifyEmail.title')}
                </PageHeading>
                <Button
                  onClick={() => {
                    setIsOpen(true);
                  }}
                  variant="secondary"
                  className="relative w-fit"
                >
                  {t('verifyEmail.verifyNow')}
                </Button>
              </div>
            </div>
          </div>
        </Section>
      </DialogTrigger>
      <DialogContent className="max-w-3xl gap-6">
        <div className="flex flex-col justify-center text-center">
          {changeEmail ? (
            <ChangeEmailModal
              closeModal={() => {
                setChangeEmail(false);
              }}
            />
          ) : (
            <>
              <div className="mb-2">
                <PageHeading>{t('verifyEmail.checkYourInbox')}</PageHeading>
              </div>

              <span className="text-primary-gray mb-9 text-sm font-light">
                <Trans
                  i18nKey="verifyEmail.emailInfo"
                  values={{ email }}
                  components={{
                    1: <span className="font-bold" />,
                  }}
                />
              </span>

              <div className="flex w-full items-center justify-center">
                <div className="mb-3 flex max-w-xs sm:max-w-md">
                  <Otp
                    error={otpError}
                    handleSubmit={() => {
                      handleSubmitOtp();
                    }}
                    numInputs={4}
                    onChange={setOtp}
                    value={otp}
                  />
                </div>
              </div>

              <div className="flex flex-col space-y-3">
                <div className="text-primary-gray gap-1 text-sm">
                  <span>{t('Didn’t get a code? ')}&nbsp;</span>
                  <Trans
                    components={{
                      1: (
                        <span
                          className="cursor-pointer text-sm hover:underline"
                          onClick={handleClickResend}
                          onKeyDown={handleKeyDown}
                          role="button"
                          tabIndex={0}
                        />
                      ),
                      2: (
                        <span
                          className="cursor-pointer text-sm hover:underline"
                          onClick={() => {
                            setChangeEmail(true);
                          }}
                          onKeyDown={() => {
                            setChangeEmail(true);
                          }}
                          role="button"
                          tabIndex={0}
                        />
                      ),
                    }}
                    i18nKey="verifyEmail.email_verification_help"
                  />
                </div>

                <Transition
                  enter="transition-opacity duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  show={codeWasSent}
                >
                  <div className="bg-primary-positive flex items-center justify-center self-center rounded-md px-3 py-1 opacity-100 transition-opacity duration-200">
                    <span className="font-medium text-white">
                      {t('verifyEmail.codeWasSent')}
                    </span>
                  </div>
                </Transition>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ChangeEmailModal({
  closeModal,
}: {
  closeModal: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [updateUser] = useUpdateEmailMutation();
  const { clearToken } = useContext(CredentialsContext);

  const schema = z.object({
    email: z.string().min(1, {
      message: t('Email is required'),
    }),
  });

  type FormData = z.infer<typeof schema>;
  const methods = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
  });
  const { handleSubmit } = methods;

  async function submit(): Promise<void> {
    try {
      await updateUser({
        variables: {
          email: methods.getValues().email,
        },
        refetchQueries: [{ query: GetViewerDocument }],
      });
      closeModal();
      enqueueSnackbar({
        message: t('Email has been changed'),
        variant: 'success',
      });
      clearToken();
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message: 'Something went wrong, email already in use or invalid',
          variant: 'error',
        });
      }
    }
  }

  function handleClickSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      await handleSubmit(submit)();
    })();
  }

  return (
    <div>
      <div className="mb-2">
        <PageHeading
          subheading={t(
            'You can only change your address once, so double check to be sure.',
          )}
        >
          {t('Change you email adress')}
        </PageHeading>
        <div>
          <FormProvider {...methods}>
            <form
              onSubmit={(e) => {
                handleClickSubmit(e);
              }}
            >
              <fieldset className="flex w-full flex-col justify-between space-y-5 py-5">
                <FormField label={t('Email')} name="email">
                  <Input placeholder={String(t('Enter your email'))} />
                </FormField>
                <div className="flex items-center justify-between">
                  <Button
                    onClick={() => {
                      closeModal();
                    }}
                    type="button"
                    variant="outlined"
                  >
                    {t('Back')}
                  </Button>
                  <Button type="submit">{t('Update')}</Button>
                </div>
              </fieldset>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}
